import { ContentExtrasBar } from '@/components/content/ContentExtrasBar';
import { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { 
  Play, 
  Pause, 
  Volume2, 
  VolumeX, 
  Maximize, 
  SkipBack, 
  SkipForward,
  X,
  CheckCircle2,
  Clock,
  Gauge,
  Subtitles,
  Check
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Progress } from '@/components/ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';

interface VideoPlayerModalProps {
  isOpen: boolean;
  contentId?: string;
  onClose: () => void;
  topic: {
    title: string;
    description: string;
    duration: string;
    index: number;
  } | null;
  onComplete: (topicTitle: string) => void;
  isViewed: boolean;
}

// Simulated subtitles data
const generateSubtitles = (topic: string): { start: number; end: number; text: string }[] => {
  const subtitles = [
    { start: 0, end: 5, text: `Bienvenidos al tema: ${topic}` },
    { start: 5, end: 12, text: "En esta lección aprenderemos conceptos fundamentales" },
    { start: 12, end: 20, text: "que te ayudarán a dominar estas habilidades" },
    { start: 20, end: 28, text: "Empecemos con los conceptos básicos..." },
    { start: 28, end: 38, text: "Es importante entender la teoría antes de la práctica" },
    { start: 38, end: 48, text: "Veamos algunos ejemplos concretos" },
    { start: 48, end: 60, text: "Recuerda tomar notas de los puntos clave" },
    { start: 60, end: 75, text: "Ahora vamos a profundizar en el tema" },
    { start: 75, end: 90, text: "Esta técnica es muy utilizada en la industria" },
    { start: 90, end: 105, text: "Practica estos conceptos regularmente" },
    { start: 105, end: 120, text: "¡Excelente! Has completado una parte importante" },
  ];
  return subtitles;
};

const playbackSpeeds = [
  { value: 0.5, label: '0.5x' },
  { value: 0.75, label: '0.75x' },
  { value: 1, label: 'Normal' },
  { value: 1.25, label: '1.25x' },
  { value: 1.5, label: '1.5x' },
  { value: 2, label: '2x' },
];

export function VideoPlayerModal({
  contentId,
  isOpen, 
  onClose, 
  topic, 
  onComplete,
  isViewed 
}: VideoPlayerModalProps) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(80);
  const [showControls, setShowControls] = useState(true);
  const [isCompleted, setIsCompleted] = useState(isViewed);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [subtitlesEnabled, setSubtitlesEnabled] = useState(true);
  const [currentSubtitle, setCurrentSubtitle] = useState<string | null>(null);
  const controlsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Parse duration string to seconds (e.g., "30 min" -> 1800)
  const totalDuration = topic ? parseInt(topic.duration) * 60 : 0;
  const progress = totalDuration > 0 ? (currentTime / totalDuration) * 100 : 0;
  
  const subtitles = topic ? generateSubtitles(topic.title) : [];

  // Update current subtitle based on time
  useEffect(() => {
    if (subtitlesEnabled && subtitles.length > 0) {
      const currentSub = subtitles.find(
        sub => currentTime >= sub.start && currentTime < sub.end
      );
      setCurrentSubtitle(currentSub?.text || null);
    } else {
      setCurrentSubtitle(null);
    }
  }, [currentTime, subtitlesEnabled, subtitles]);

  // Simulate video playback with speed adjustment
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isPlaying && currentTime < totalDuration) {
      interval = setInterval(() => {
        setCurrentTime(prev => {
          const newTime = prev + playbackSpeed;
          // Mark as complete when reaching 90% of the video
          if (newTime >= totalDuration * 0.9 && !isCompleted && topic) {
            setIsCompleted(true);
            onComplete(topic.title);
          }
          return Math.min(newTime, totalDuration);
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isPlaying, currentTime, totalDuration, isCompleted, topic, onComplete, playbackSpeed]);

  // Auto-hide controls
  useEffect(() => {
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isPlaying, showControls]);

  const handleMouseMove = () => {
    setShowControls(true);
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleSeek = (value: number[]) => {
    const newTime = (value[0] / 100) * totalDuration;
    setCurrentTime(newTime);
  };

  const handleSkip = (seconds: number) => {
    setCurrentTime(prev => Math.max(0, Math.min(totalDuration, prev + seconds)));
  };

  const handleClose = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    setIsCompleted(isViewed);
    setPlaybackSpeed(1);
    onClose();
  };

  const gradientColors = [
    'from-violet-600 via-purple-600 to-indigo-700',
    'from-rose-600 via-pink-600 to-fuchsia-700',
    'from-cyan-600 via-teal-600 to-emerald-700',
    'from-amber-600 via-orange-600 to-red-700',
    'from-blue-600 via-indigo-600 to-violet-700',
  ];

  if (!topic) return null;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-5xl w-full p-0 bg-black border-none overflow-hidden"
        onMouseMove={handleMouseMove}
      >
        {contentId && <ContentExtrasBar contentId={contentId} description={topic.description} />}
        {/* Video Container */}
        <div className="relative aspect-video bg-black">
          {/* Simulated Video Background */}
          <div className={cn(
            "absolute inset-0 bg-gradient-to-br",
            gradientColors[topic.index % gradientColors.length]
          )}>
            {/* Animated pattern overlay */}
            <div className="absolute inset-0 opacity-20" 
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
              }} 
            />
            {/* Animated circles for visual interest */}
            <div className="absolute inset-0 overflow-hidden">
              <div className={cn(
                "absolute w-96 h-96 rounded-full bg-white/10 blur-3xl",
                isPlaying ? "animate-pulse" : ""
              )} style={{ top: '10%', left: '20%' }} />
              <div className={cn(
                "absolute w-64 h-64 rounded-full bg-white/10 blur-3xl",
                isPlaying ? "animate-pulse" : ""
              )} style={{ bottom: '10%', right: '20%', animationDelay: '1s' }} />
            </div>
          </div>

          {/* Topic Info Overlay */}
          <div className={cn(
            "absolute inset-0 flex flex-col items-center justify-center text-white transition-opacity duration-300",
            isPlaying && !showControls ? "opacity-0" : "opacity-100"
          )}>
            <span className="text-white/60 text-sm font-medium mb-2">
              Capítulo {String(topic.index + 1).padStart(2, '0')}
            </span>
            <h2 className="text-3xl font-bold mb-2 text-center px-8">{topic.title}</h2>
            <p className="text-white/70 text-center max-w-xl px-8">{topic.description}</p>
            
            {/* Big Play Button */}
            {!isPlaying && (
              <button
                onClick={() => setIsPlaying(true)}
                className="mt-8 w-20 h-20 rounded-full bg-white/20 hover:bg-white/30 backdrop-blur-sm flex items-center justify-center transition-all hover:scale-110 group"
              >
                <Play className="w-10 h-10 text-white fill-white ml-1 group-hover:scale-110 transition-transform" />
              </button>
            )}

            {/* Completed Badge */}
            {isCompleted && (
              <div className="mt-6 bg-success/90 backdrop-blur-sm text-white px-4 py-2 rounded-full flex items-center gap-2 animate-scale-in">
                <CheckCircle2 className="w-5 h-5" />
                <span className="font-medium">Tema Completado</span>
              </div>
            )}
          </div>

          {/* Subtitles Display */}
          {subtitlesEnabled && currentSubtitle && isPlaying && (
            <div className="absolute bottom-24 left-0 right-0 flex justify-center px-8 animate-fade-in">
              <div className="bg-black/80 backdrop-blur-sm text-white px-6 py-3 rounded-lg text-lg font-medium text-center max-w-2xl">
                {currentSubtitle}
              </div>
            </div>
          )}

          {/* Close Button */}
          <button
            onClick={handleClose}
            className={cn(
              "absolute top-4 right-4 w-10 h-10 rounded-full bg-black/50 hover:bg-black/70 flex items-center justify-center transition-all",
              showControls ? "opacity-100" : "opacity-0"
            )}
          >
            <X className="w-5 h-5 text-white" />
          </button>

          {/* Playback Speed Badge */}
          {playbackSpeed !== 1 && (
            <div className={cn(
              "absolute top-4 left-4 bg-black/50 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-medium transition-opacity",
              showControls ? "opacity-100" : "opacity-0"
            )}>
              <div className="flex items-center gap-1.5">
                <Gauge className="w-4 h-4" />
                {playbackSpeed}x
              </div>
            </div>
          )}

          {/* Progress Bar at top (Netflix style) */}
          <div className={cn(
            "absolute top-0 left-0 right-0 transition-opacity duration-300",
            showControls ? "opacity-100" : "opacity-0"
          )}>
            <Progress value={progress} variant="gradient" className="h-1 rounded-none" />
          </div>

          {/* Controls Bar */}
          <div className={cn(
            "absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent p-4 transition-opacity duration-300",
            showControls ? "opacity-100" : "opacity-0"
          )}>
            {/* Seek Bar */}
            <div className="mb-4 group">
              <Slider
                value={[progress]}
                onValueChange={handleSeek}
                max={100}
                step={0.1}
                className="cursor-pointer"
              />
            </div>

            <div className="flex items-center justify-between">
              {/* Left Controls */}
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={() => setIsPlaying(!isPlaying)}
                >
                  {isPlaying ? (
                    <Pause className="w-6 h-6" />
                  ) : (
                    <Play className="w-6 h-6 ml-0.5" />
                  )}
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={() => handleSkip(-10)}
                >
                  <SkipBack className="w-5 h-5" />
                </Button>

                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20"
                  onClick={() => handleSkip(10)}
                >
                  <SkipForward className="w-5 h-5" />
                </Button>

                {/* Volume */}
                <div className="flex items-center gap-2 group/volume">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white hover:bg-white/20"
                    onClick={() => setIsMuted(!isMuted)}
                  >
                    {isMuted || volume === 0 ? (
                      <VolumeX className="w-5 h-5" />
                    ) : (
                      <Volume2 className="w-5 h-5" />
                    )}
                  </Button>
                  <div className="w-0 overflow-hidden group-hover/volume:w-24 transition-all duration-300">
                    <Slider
                      value={[isMuted ? 0 : volume]}
                      onValueChange={(v) => {
                        setVolume(v[0]);
                        if (v[0] > 0) setIsMuted(false);
                      }}
                      max={100}
                      className="cursor-pointer"
                    />
                  </div>
                </div>

                {/* Time */}
                <span className="text-white/80 text-sm font-mono ml-2">
                  {formatTime(currentTime)} / {formatTime(totalDuration)}
                </span>
              </div>

              {/* Right Controls */}
              <div className="flex items-center gap-1">
                <div className="flex items-center gap-1.5 text-white/70 text-sm mr-2">
                  <Clock className="w-4 h-4" />
                  <span>{topic.duration}</span>
                </div>

                {/* Subtitles Toggle */}
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "text-white hover:bg-white/20 relative",
                    subtitlesEnabled && "text-secondary"
                  )}
                  onClick={() => setSubtitlesEnabled(!subtitlesEnabled)}
                >
                  <Subtitles className="w-5 h-5" />
                  {subtitlesEnabled && (
                    <span className="absolute -bottom-0.5 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-secondary rounded-full" />
                  )}
                </Button>

                {/* Playback Speed */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white hover:bg-white/20"
                    >
                      <Gauge className="w-5 h-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="end" 
                    className="bg-black/90 backdrop-blur-xl border-white/20 text-white min-w-[140px]"
                  >
                    <DropdownMenuLabel className="text-white/60 text-xs">
                      Velocidad de reproducción
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="bg-white/10" />
                    {playbackSpeeds.map((speed) => (
                      <DropdownMenuItem
                        key={speed.value}
                        onClick={() => setPlaybackSpeed(speed.value)}
                        className={cn(
                          "cursor-pointer hover:bg-white/10 focus:bg-white/10",
                          playbackSpeed === speed.value && "text-secondary"
                        )}
                      >
                        <div className="flex items-center justify-between w-full">
                          <span>{speed.label}</span>
                          {playbackSpeed === speed.value && (
                            <Check className="w-4 h-4 text-secondary" />
                          )}
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>

                <Button
                  variant="ghost"
                  size="icon"
                  className="text-white hover:bg-white/20"
                >
                  <Maximize className="w-5 h-5" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
